import {
  Modal,
  Image,
  Skeleton,
  Button,
  useMantineTheme,
  Tabs,
  Title,
  Paper,
  Group,
  Stack,
  Divider,
  Text,
  Center,
  TextInput,
} from '@mantine/core';
import { useClipboard, useMediaQuery } from '@mantine/hooks';
import { Session } from '@supabase/supabase-js';
import { IconBrandTwitter, IconCamera, IconLink } from '@tabler/icons';
import { useEffect } from 'react';
import { isIOS, isMobile, isSafari } from 'react-device-detect';
import { useLog } from '../../LogEventContext';
import { useStore } from '../../stores/stores';
import { Database } from '../../types/supabase';
import TwitterCard from './TwitterCard';

export default function ShareModal({
  openedValue,
  onClose,
  tierListData,
  shareImage,
  save_id,
  session,
  onShareList,
}: {
  openedValue: string;
  onClose: (loginPending?: string) => void;
  tierListData: Database['public']['Tables']['tier_lists']['Row'];
  shareImage: string | null;
  save_id: number | null;
  session: Session | null;
  onShareList: (justUpdate?: boolean, modalShare?: boolean) => void;
}) {
  const theme = useMantineTheme();
  const clipboard = useClipboard({ timeout: 500 });
  const clipboard2 = useClipboard({ timeout: 500 });
  const baseUrl = `https://lomolist.com/${tierListData.id}/${tierListData.slug}`;
  const fullUrl = `https://lomolist.com/${tierListData.id}/${tierListData.slug}${
    save_id ? `/list/${save_id}` : ''
  }`;
  const { LogEvent } = useLog();
  const isLoggedIn = session?.user;
  const shareToTwitter = (usingFull: boolean) => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `Check out my ${tierListData.name} ${usingFull ? 'Tier List' : 'Template'}!`
      )}&hashtags=LomoList&url=${encodeURIComponent(usingFull ? fullUrl : baseUrl)}`
    );
  };

  useEffect(() => {
    if (openedValue) {
      LogEvent('open_share_modal', openedValue);
    }
  }, [openedValue]);
  const { setLoginOpen } = useStore(null);
  const isSmall = useMediaQuery('(max-width: 768px)');
  return (
    <Modal
      size="xl"
      opened={!!openedValue}
      onClose={onClose}
      title={
        <Title color="dimmed" order={3}>
          Share your Lomo List!
        </Title>
      }
      centered
      overlayProps={{
        color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
        opacity: 0.65,
      }}
    >
      <Tabs orientation={isSmall ? 'horizontal' : 'vertical'} defaultValue={openedValue}>
        <Tabs.List>
          <Tabs.Tab
            value="twitter"
            icon={<IconBrandTwitter size="0.8rem" />}
            // sx={{
            //   '&:hover': {
            //     backgroundColor: theme.colors.blue[6],
            //   },
            //   color: 'white',
            //   backgroundColor: theme.colors.blue[6],
            // }}
          >
            Twitter
          </Tabs.Tab>
          <Tabs.Tab value="link" icon={<IconLink size="0.8rem" />}>
            Share Link
          </Tabs.Tab>
          <Tabs.Tab value="image" icon={<IconCamera size="0.8rem" />}>
            Download Image
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="link" pl="xs">
          <Paper h={600} pt="lg">
            <Center>
              <Stack w="100%" maw={500} sx={{ justifyContent: 'space-between' }}>
                <Stack align="center">
                  <Title color={theme.colorScheme === 'dark' ? 'violet.3' : 'violet.7'}>
                    {' '}
                    Share your Tier List
                  </Title>
                  <Text align="center" color="dimmed">
                    {' '}
                    {!isLoggedIn
                      ? 'To share your Tier List and/or save it to your profile you need to be signed in.'
                      : 'Users will be able to see where your ranked the images. Tier List will save to your profile.'}
                  </Text>
                  {isLoggedIn && <TextInput w="100%" maw={350} defaultValue={fullUrl} />}
                  {isLoggedIn ? (
                    <Button
                      color={clipboard.copied ? 'teal' : 'mainViolet'}
                      onClick={() => {
                        clipboard.copy(fullUrl);
                        onShareList(false, true);
                        LogEvent('share_tier_list', 'copy');
                      }}
                    >
                      {clipboard.copied ? 'Link Copied' : 'Copy link'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setLoginOpen(true);
                        onClose('link');
                        LogEvent('share_tier_list', 'login');
                      }}
                    >
                      Log In
                    </Button>
                  )}
                </Stack>

                <Divider size="xl" my="md" />

                <Stack align="center">
                  <Title order={2}> Share this Template</Title>
                  <Text color="dimmed">
                    {' '}
                    Share a link to this Template without including your ranking.
                  </Text>
                  <TextInput w="100%" maw={350} defaultValue={baseUrl} />
                  <Button
                    variant="outline"
                    color={clipboard2.copied ? 'teal' : 'mainViolet'}
                    onClick={() => {
                      clipboard2.copy(baseUrl);
                      LogEvent('share_template', 'copy');
                    }}
                  >
                    {clipboard2.copied ? 'Link Copied' : 'Copy link'}
                  </Button>
                </Stack>
              </Stack>
            </Center>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="twitter" pl="xs">
          <Paper h={600} pt="lg">
            <Center>
              <Stack w="100%" maw={500} sx={{ justifyContent: 'space-between' }} pb="lg">
                <Stack align="center" sx={{ gap: 0 }}>
                  <Title align="center" color={theme.colorScheme === 'dark' ? 'blue.3' : 'blue.7'}>
                    {' '}
                    Tweet your Tier List
                  </Title>
                  <Text align="center" color="dimmed" my="md">
                    Tweeting a link to your Tier List allows other users to view your Tier List on
                    Lomo. Twitter will include an image of your Tier List.
                  </Text>
                  {isLoggedIn ? (
                    <Button
                      variant="gradient"
                      mb="md"
                      onClick={() => {
                        shareToTwitter(true);
                        onShareList(false, true);
                        LogEvent('share_tier_list', 'twitter');
                      }}
                    >
                      Share to Twitter
                    </Button>
                  ) : (
                    <Button
                      variant="gradient"
                      mb="md"
                      onClick={() => {
                        setLoginOpen(true);
                        onClose('twitter');
                        LogEvent('share_tier_list', 'login_twitter');
                      }}
                    >
                      Login to Share
                    </Button>
                  )}

                  <TwitterCard shareImage={shareImage} tierListData={tierListData} />
                  {/* <Image height={200} src="" withPlaceholder placeholder={<Skeleton h={200} />} /> */}
                </Stack>
                <Divider size="xl" />
                <Stack align="center">
                  <Title order={2}> Tweet this Template</Title>
                  <Text color="dimmed">
                    {' '}
                    Share a link to this Template without including your ranking.
                  </Text>
                  <Button
                    variant="outline"
                    color="blue"
                    onClick={() => {
                      shareToTwitter(false);
                      LogEvent('share_template', 'twitter');
                    }}
                  >
                    Share to Twitter
                  </Button>
                </Stack>
              </Stack>
            </Center>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="image" pl="xs">
          <Paper h={600} pt="lg">
            <Center>
              <Stack w="100%" sx={{ justifyContent: 'space-between' }} pb="lg">
                <Title color={theme.colorScheme === 'dark' ? 'violet.3' : 'violet.7'}>
                  {' '}
                  Image Preview
                </Title>
                {shareImage ? (
                  <Image width="100%" height="100%" src={shareImage} alt="Tier List" />
                ) : (
                  <Skeleton width="100%" height={500} />
                )}
                <Text color="dimmed" mt="lg" mb="md">
                  {' '}
                  Make sure to link to this Tier List so users can complete their own!
                </Text>
                <Group>
                  {isMobile && <Text size="xs">Tap and hold to save the image.</Text>}
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.download = `${tierListData.name} Tier List (LomoList).png`;
                      link.href = shareImage ?? '';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      LogEvent('share_image', 'download');
                    }}
                  >
                    Download Image
                  </Button>
                  {isLoggedIn && (
                    <Button
                      variant="outline"
                      color={clipboard.copied ? 'teal' : 'mainViolet'}
                      onClick={() => {
                        clipboard.copy(fullUrl);
                        onShareList(false, true);
                      }}
                    >
                      {clipboard.copied ? 'Link Copied' : 'Copy link'}
                    </Button>
                  )}
                </Group>
              </Stack>
            </Center>
          </Paper>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}
